import "../Dashboard.css";
import "../EvaluationAnalytics.css";

import axios from "axios";
import { useAuth } from "../provider/authProvider";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import flatpickr from "flatpickr";
import { Spanish } from "flatpickr/dist/l10n/es.js";
import "flatpickr/dist/themes/material_blue.css";
import moment from "moment-timezone";
import "moment/locale/es";
import Select from "react-select";

import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    ComposedChart,
    LabelList,
    Legend,
    Line,
    Pie,
    PieChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";

moment.locale("es");

const API = process.env.REACT_APP_API || "";
const TZ = "America/Lima";

const COLORS = [
    "#6d28d9",
    "#2563eb",
    "#16a34a",
    "#f97316",
    "#dc2626",
    "#0891b2",
    "#ca8a04",
    "#db2777",
];

const COMPARE_COLORS = ["#6d28d9", "#2563eb", "#16a34a"];

const PEN = new Intl.NumberFormat("es-PE", {
    style: "currency",
    currency: "PEN",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
});

const compactNumber = new Intl.NumberFormat("es-PE", {
    maximumFractionDigits: 0,
});

const percentNumber = new Intl.NumberFormat("es-PE", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
});

const getToday = () => moment.tz(TZ).format("YYYY-MM-DD");
const getMonthStart = () => moment.tz(TZ).startOf("month").format("YYYY-MM-DD");

const shortName = (value, limit = 18) => {
    const text = String(value || "Sin registro").trim();
    return text.length > limit ? `${text.slice(0, limit)}...` : text;
};

const buildCalendarText = (startDate, endDate) => {
    if (!startDate || !endDate) return "Selecciona un rango de fechas";

    const start = moment.tz(startDate, "YYYY-MM-DD", TZ);
    const end = moment.tz(endDate, "YYYY-MM-DD", TZ);

    if (start.isSame(end, "day")) {
        return `Mostrando ${start.format("dddd DD [de] MMMM [del] YYYY")}`;
    }

    return `Mostrando del ${start.format("DD [de] MMMM")} al ${end.format(
        "DD [de] MMMM [del] YYYY",
    )}`;
};

const getYearOptions = () => {
    const currentYear = moment.tz(TZ).year();
    const startYear = 2024;

    return Array.from({ length: currentYear - startYear + 2 }, (_, index) => {
        const year = startYear + index;
        return { value: year, label: String(year) };
    }).reverse();
};

const allSedesOption = {
    value: "all",
    label: "Todas las sedes",
    ciudad: "General",
};

const allCitiesOption = {
    value: "all",
    label: "Todas las ciudades",
};

const MONTHS_ES = {
    1: { name: "Enero", short: "Ene" },
    2: { name: "Febrero", short: "Feb" },
    3: { name: "Marzo", short: "Mar" },
    4: { name: "Abril", short: "Abr" },
    5: { name: "Mayo", short: "May" },
    6: { name: "Junio", short: "Jun" },
    7: { name: "Julio", short: "Jul" },
    8: { name: "Agosto", short: "Ago" },
    9: { name: "Septiembre", short: "Sep" },
    10: { name: "Octubre", short: "Oct" },
    11: { name: "Noviembre", short: "Nov" },
    12: { name: "Diciembre", short: "Dic" },
};

const getMonthNameEs = (month, fallback = "Sin mes") => {
    const numericMonth = Number(month || 0);
    return MONTHS_ES[numericMonth]?.name || fallback || "Sin mes";
};

const getMonthShortEs = (month, fallback = "") => {
    const numericMonth = Number(month || 0);
    return MONTHS_ES[numericMonth]?.short || fallback || "";
};

const selectStyles = {
    control: (base, state) => ({
        ...base,
        minHeight: 50,
        borderRadius: 16,
        borderColor: state.isFocused ? "#6d28d9" : "#d8d3ea",
        boxShadow: state.isFocused ? "0 0 0 4px rgba(109, 40, 217, 0.12)" : "none",
        "&:hover": { borderColor: "#6d28d9" },
    }),
    valueContainer: (base) => ({
        ...base,
        padding: "4px 12px",
    }),
    multiValue: (base) => ({
        ...base,
        borderRadius: 999,
        backgroundColor: "rgba(109, 40, 217, 0.1)",
    }),
    multiValueLabel: (base) => ({
        ...base,
        color: "#4c1d95",
        fontWeight: 800,
    }),
    menu: (base) => ({
        ...base,
        zIndex: 9999,
        borderRadius: 16,
        overflow: "hidden",
    }),
    menuPortal: (base) => ({
        ...base,
        zIndex: 9999,
    }),
};

const EmptyChart = ({
    text = "No hay ventas para mostrar con el rango seleccionado.",
}) => (
    <div className="evaluationGlobalEmptyChart">
        <strong>Sin datos</strong>
        <p>{text}</p>
    </div>
);

const KpiCard = ({ title, value, subtitle, variant }) => (
    <article className={`evaluationGlobalKpiCard ${variant || ""}`}>
        <span>{title}</span>
        <strong>{value}</strong>
        {subtitle ? <small>{subtitle}</small> : null}
    </article>
);

const ChartTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;

    const row = payload[0]?.payload || {};

    return (
        <div className="evaluationGlobalTooltip">
            <strong>{row.name || label || "Sin registro"}</strong>

            {payload.map((item) => {
                const key = String(item?.dataKey || "").toLowerCase();
                const value = Number(item?.value || 0);
                const isMoney = key.includes("amount") || key.includes("ticket");

                return (
                    <p key={`${item.dataKey}-${item.name}`}>
                        {item.name}:{" "}
                        {isMoney ? PEN.format(value) : compactNumber.format(value)}
                    </p>
                );
            })}
        </div>
    );
};

const AnnualGrowthTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;

    const row = payload[0]?.payload || {};
    const tooltipRows = Object.values(row.__tooltip || {});

    return (
        <div className="evaluationAnnualTooltip">
            <strong>{label}</strong>

            {tooltipRows.map((item) => {
                const growth = Number(item.growthPercent || 0);
                const growthClass = growth >= 0 ? "positive" : "negative";

                return (
                    <div className="evaluationAnnualTooltipBlock" key={item.key}>
                        <b>{item.sede}</b>
                        <p>Ventas del mes: {compactNumber.format(item.sales || 0)}</p>
                        <p>Mes anterior: {compactNumber.format(item.previousSales || 0)}</p>
                        <p>
                            Diferencia: {Number(item.difference || 0) > 0 ? "+" : ""}
                            {compactNumber.format(item.difference || 0)}
                        </p>
                        <p className={growthClass}>
                            Crecimiento: {percentNumber.format(growth)}%
                        </p>
                        <p>
                            Participación: {percentNumber.format(item.participation || 0)}%
                        </p>
                    </div>
                );
            })}
        </div>
    );
};

const renderTopBarLabel = ({ x, y, width, value, money }) => {
    const numericValue = Number(value || 0);

    if (!numericValue) return null;

    return (
        <text
            x={Number(x || 0) + Number(width || 0) / 2}
            y={Number(y || 0) - 8}
            textAnchor="middle"
            className="evaluationChartTopLabel"
        >
            {money ? PEN.format(numericValue) : compactNumber.format(numericValue)}
        </text>
    );
};

const renderAnnualBarLabel = ({
    x,
    y,
    width,
    value,
    index,
    chartData,
    seriesKey,
}) => {
    const numericValue = Number(value || 0);

    if (!numericValue) return null;

    const key = String(seriesKey || "").replace("sales_", "");
    const row = Array.isArray(chartData) ? chartData[Number(index || 0)] : null;
    const tooltipItem = row?.__tooltip?.[key] || {};
    const growth = Number(tooltipItem.growthPercent || 0);
    const growthPrefix = growth > 0 ? "+" : "";
    const centerX = Number(x || 0) + Number(width || 0) / 2;
    const topY = Number(y || 0) - 36;
    const growthText = `${growthPrefix}${percentNumber.format(growth)}%`;

    return (
        <g className="evaluationAnnualBarLabelGroup" pointerEvents="none">
            <text
                x={centerX}
                y={topY}
                textAnchor="middle"
                className="evaluationAnnualSalesLabel"
            >
                {compactNumber.format(numericValue)}
            </text>

            <rect
                x={centerX - 28}
                y={topY + 5}
                width="56"
                height="18"
                rx="9"
                className={
                    growth < 0
                        ? "evaluationAnnualGrowthBadge negative"
                        : "evaluationAnnualGrowthBadge positive"
                }
            />

            <text
                x={centerX}
                y={topY + 18}
                textAnchor="middle"
                className="evaluationAnnualGrowthLabel"
            >
                {growthText}
            </text>
        </g>
    );
};

const GrowthDot = (props) => {
    const { cx, cy, payload, dataKey, stroke } = props;
    const key = String(dataKey || "").replace("growth_", "");
    const item = payload?.__tooltip?.[key];
    const growth = Number(item?.growthPercent || 0);

    if (cx == null || cy == null) return null;

    return (
        <circle
            cx={cx}
            cy={cy}
            r={growth > 100 ? 7 : 4}
            fill="#ffffff"
            stroke={stroke}
            strokeWidth={growth > 100 ? 4 : 2}
            className={growth > 100 ? "evaluationAnnualHotDot" : ""}
        />
    );
};

const SalesBarChart = ({
    data = [],
    dataKey = "totalSales",
    money = false,
}) => {
    if (!data.length) return <EmptyChart />;

    return (
        <ResponsiveContainer width="100%" height={330}>
            <BarChart
                data={data}
                margin={{ top: 36, right: 18, left: 0, bottom: 18 }}
            >
                <CartesianGrid strokeDasharray="3 3" vertical={false} />

                <XAxis
                    dataKey="shortName"
                    tick={{ fontSize: 12 }}
                    interval={0}
                    angle={-12}
                    textAnchor="end"
                    height={60}
                />

                <YAxis
                    tick={{ fontSize: 12 }}
                    tickFormatter={(value) =>
                        money ? `S/${Number(value || 0).toFixed(0)}` : value
                    }
                />

                <Tooltip content={<ChartTooltip />} />
                <Legend />

                <Bar
                    dataKey={dataKey}
                    name={money ? "Monto vendido" : "Ventas"}
                    radius={[12, 12, 0, 0]}
                >
                    <LabelList
                        dataKey={dataKey}
                        content={(props) => renderTopBarLabel({ ...props, money })}
                    />

                    {data.map((_, index) => (
                        <Cell key={`bar-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                </Bar>
            </BarChart>
        </ResponsiveContainer>
    );
};

const SalesPieChart = ({ data = [] }) => {
    if (!data.length) return <EmptyChart />;

    return (
        <ResponsiveContainer width="100%" height={330}>
            <PieChart>
                <Pie
                    data={data}
                    dataKey="totalSales"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={112}
                    innerRadius={58}
                    paddingAngle={3}
                    label={({ name, percent }) =>
                        `${shortName(name, 12)} ${(Number(percent || 0) * 100).toFixed(1)}%`
                    }
                >
                    {data.map((_, index) => (
                        <Cell key={`pie-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                </Pie>

                <Tooltip content={<ChartTooltip />} />
                <Legend />
            </PieChart>
        </ResponsiveContainer>
    );
};

function AnnualGrowthSalesDashboard() {
    const { rol } = useAuth();
    const currentYear = moment.tz(TZ).year();

    const [year, setYear] = useState(currentYear);
    const [selectedSedes, setSelectedSedes] = useState([allSedesOption]);
    const [selectedCities, setSelectedCities] = useState([allCitiesOption]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [annualData, setAnnualData] = useState({
        summary: {},
        sedeOptions: [],
        cityOptions: [],
        selectedSedes: [allSedesOption],
        selectedCities: [allCitiesOption],
        consolidatedRows: [],
        rows: [],
        cityRows: [],
    });

    const isAdmin = String(rol) === "1";
    const isAllSedes = selectedSedes.some((item) => item.value === "all");
    const isAllCities = selectedCities.some((item) => item.value === "all");
    const comparisonMode = !isAllCities ? "city" : !isAllSedes ? "sede" : "all";

    const sedeOptions = useMemo(
        () => [allSedesOption, ...(annualData.sedeOptions || [])],
        [annualData.sedeOptions],
    );

    const cityOptions = useMemo(
        () => [allCitiesOption, ...(annualData.cityOptions || [])],
        [annualData.cityOptions],
    );

    const yearOptions = useMemo(() => getYearOptions(), []);

    const handleSedesChange = useCallback((values, actionMeta) => {
        const nextValues = Array.isArray(values) ? values : [];
        const selectedValue = actionMeta?.option?.value;
        const action = actionMeta?.action;

        if (!nextValues.length) {
            setSelectedSedes([allSedesOption]);
            return;
        }

        if (selectedValue === "all" && action === "select-option") {
            setSelectedSedes([allSedesOption]);
            return;
        }

        const cleanValues = nextValues
            .filter((item) => item.value !== "all")
            .slice(0, 3);

        setSelectedSedes(cleanValues.length ? cleanValues : [allSedesOption]);
        setSelectedCities([allCitiesOption]);
    }, []);

    const handleCitiesChange = useCallback((values, actionMeta) => {
        const nextValues = Array.isArray(values) ? values : [];
        const selectedValue = actionMeta?.option?.value;
        const action = actionMeta?.action;

        if (!nextValues.length) {
            setSelectedCities([allCitiesOption]);
            return;
        }

        if (selectedValue === "all" && action === "select-option") {
            setSelectedCities([allCitiesOption]);
            return;
        }

        const cleanValues = nextValues
            .filter((item) => item.value !== "all")
            .slice(0, 3);

        setSelectedCities(cleanValues.length ? cleanValues : [allCitiesOption]);
        setSelectedSedes([allSedesOption]);
    }, []);

    const fetchAnnualGrowth = useCallback(async () => {
        if (!isAdmin) return;

        setLoading(true);
        setError("");

        try {
            const token = localStorage.getItem("token");
            const sedeIds =
                comparisonMode === "sede"
                    ? selectedSedes.map((item) => item.value).join(",")
                    : "all";
            const cityKeys =
                comparisonMode === "city"
                    ? selectedCities
                        .map((item) => encodeURIComponent(item.value))
                        .join(",")
                    : "all";

            const { data } = await axios.get(
                `${API}/evaluation-analytics/annual-growth`,
                {
                    headers: { Authorization: `Bearer ${token}` },
                    params: { year, sedeIds, cityKeys },
                },
            );

            setAnnualData({
                summary: data?.summary || {},
                sedeOptions: data?.sedeOptions || [],
                cityOptions: data?.cityOptions || [],
                selectedSedes: data?.selectedSedes || [allSedesOption],
                selectedCities: data?.selectedCities || [allCitiesOption],
                consolidatedRows: data?.consolidatedRows || [],
                rows: data?.rows || [],
                cityRows: data?.cityRows || [],
            });
        } catch (err) {
            console.error("Error cargando análisis anual:", err);

            setError(
                err?.response?.data?.error ||
                "No se pudo cargar el análisis de crecimiento anual.",
            );
        } finally {
            setLoading(false);
        }
    }, [comparisonMode, isAdmin, selectedCities, selectedSedes, year]);

    useEffect(() => {
        fetchAnnualGrowth();
    }, [fetchAnnualGrowth]);

    const activeSeries = useMemo(() => {
        if (comparisonMode === "all") return [allSedesOption];

        if (comparisonMode === "city") {
            return selectedCities.slice(0, 3).map((item) => ({
                ...item,
                label: item.label || "Sin ciudad",
            }));
        }

        return selectedSedes.slice(0, 3).map((item) => ({
            ...item,
            label: item.label || "Sin sede",
        }));
    }, [comparisonMode, selectedCities, selectedSedes]);

    const chartData = useMemo(() => {
        const sourceRows =
            comparisonMode === "all"
                ? annualData.consolidatedRows || []
                : comparisonMode === "city"
                    ? annualData.cityRows || []
                    : annualData.rows || [];

        const rowsByMonth = sourceRows.reduce((acc, item) => {
            const monthKey = item.month_key || `${item.year}-${item.month}`;
            const seriesKey =
                comparisonMode === "all"
                    ? "all"
                    : String(item.series_id || item.sede_id);

            if (!acc[monthKey]) {
                acc[monthKey] = {
                    monthKey,
                    monthLabel: `${getMonthShortEs(
                        item.month,
                        item.month_short || item.month_name,
                    ).toUpperCase()} ${item.year}`,
                    monthName: getMonthNameEs(item.month, item.month_name),
                    __tooltip: {},
                };
            }

            acc[monthKey][`sales_${seriesKey}`] = Number(item.total_ventas || 0);
            acc[monthKey][`growth_${seriesKey}`] = Number(
                item.crecimiento_porcentaje || 0,
            );
            acc[monthKey].__tooltip[seriesKey] = {
                key: seriesKey,
                sede:
                    comparisonMode === "city"
                        ? item.ciudad || item.sede || "Sin ciudad"
                        : item.sede || "Todas las sedes",
                monthName: getMonthNameEs(item.month, item.month_name),
                sales: Number(item.total_ventas || 0),
                previousSales: Number(item.ventas_mes_anterior || 0),
                difference: Number(item.diferencia_ventas || 0),
                growthPercent: Number(item.crecimiento_porcentaje || 0),
                participation: Number(item.participacion_porcentaje || 0),
            };

            return acc;
        }, {});

        return Object.values(rowsByMonth);
    }, [
        annualData.cityRows,
        annualData.consolidatedRows,
        annualData.rows,
        comparisonMode,
    ]);

    const hasAnnualData = useMemo(
        () =>
            chartData.some((item) =>
                activeSeries.some(
                    (serie) => Number(item[`sales_${serie.value}`] || 0) > 0,
                ),
            ),
        [activeSeries, chartData],
    );

    const summary = annualData.summary || {};
    const bestMonth = summary.bestMonth || {};
    const bestSede = summary.bestSede || {};
    const bestGrowthSede = summary.bestGrowthSede || {};

    if (!isAdmin) return null;

    return (
        <section className="evaluationAnnualSection">
            <div className="evaluationAnnualHeader">
                <div className="evaluationAnnualHeaderOneBox">
                    <span className="evaluationGlobalEyebrow">Crecimiento anual</span>
                    <h2>Análisis de crecimiento anual de ventas</h2>
                    <p>
                        Evolución mensual por ciudad o sede, comparación hasta 3 elementos y
                        crecimiento porcentual del año seleccionado.
                    </p>
                </div>

                <div className="evaluationAnnualFilters">
                    <div className="evaluationAnnualFilterItem">
                        <label>Año</label>
                        <Select
                            styles={selectStyles}
                            value={yearOptions.find((item) => item.value === year)}
                            options={yearOptions}
                            onChange={(option) =>
                                setYear(Number(option?.value || currentYear))
                            }
                            isSearchable={false}
                        />
                    </div>

                    <div className="evaluationAnnualFilterItem evaluationAnnualFilterCities">
                        <label>Ciudades</label>
                        <Select
                            styles={selectStyles}
                            value={selectedCities}
                            options={cityOptions}
                            onChange={handleCitiesChange}
                            isMulti
                            closeMenuOnSelect={false}
                            placeholder="Todas las ciudades"
                            noOptionsMessage={() => "Sin ciudades disponibles"}
                            menuPortalTarget={document.body}
                            classNamePrefix="evaluationAnnualCitiesSelect"
                            isOptionDisabled={(option) =>
                                option.value !== "all" &&
                                !isAllCities &&
                                selectedCities.length >= 3 &&
                                !selectedCities.some((item) => item.value === option.value)
                            }
                        />
                        <small>Comparación limitada a máximo 3 ciudades.</small>
                    </div>

                    <div className="evaluationAnnualFilterItem evaluationAnnualFilterSedes">
                        <label>Sedes</label>
                        <Select
                            styles={selectStyles}
                            value={selectedSedes}
                            options={sedeOptions}
                            onChange={handleSedesChange}
                            isMulti
                            closeMenuOnSelect={false}
                            placeholder="Todas las sedes"
                            noOptionsMessage={() => "Sin sedes disponibles"}
                            menuPortalTarget={document.body}
                            classNamePrefix="evaluationAnnualSedesSelect"
                            isOptionDisabled={(option) =>
                                option.value !== "all" &&
                                !isAllSedes &&
                                selectedSedes.length >= 3 &&
                                !selectedSedes.some((item) => item.value === option.value)
                            }
                        />
                        <small>Comparación limitada a máximo 3 sedes.</small>
                    </div>
                </div>
            </div>

            {error ? <div className="evaluationGlobalAlert">{error}</div> : null}

            <div className="evaluationAnnualKpiGrid">
                <KpiCard
                    title="Total ventas del año"
                    value={compactNumber.format(summary.totalSales || 0)}
                    subtitle={
                        Number(year) === currentYear
                            ? "Acumulado hasta el mes actual"
                            : "Enero a diciembre"
                    }
                    variant="primary"
                />

                <KpiCard
                    title="Crecimiento promedio"
                    value={`${percentNumber.format(summary.averageGrowth || 0)}%`}
                    subtitle="Promedio de meses con base anterior"
                />

                <KpiCard
                    title={
                        comparisonMode === "city"
                            ? "Mejor ciudad del año"
                            : "Mejor sede del año"
                    }
                    value={
                        comparisonMode === "city"
                            ? summary.bestCity?.ciudad || "Sin ventas"
                            : bestSede?.sede || "Sin ventas"
                    }
                    subtitle={
                        comparisonMode === "city"
                            ? summary.bestCity?.total_ventas
                                ? `${compactNumber.format(summary.bestCity.total_ventas)} ventas`
                                : "Sin información"
                            : bestSede?.total_ventas
                                ? `${compactNumber.format(bestSede.total_ventas)} ventas`
                                : "Sin información"
                    }
                />

                <KpiCard
                    title="Mes con más ventas"
                    value={getMonthNameEs(
                        bestMonth?.month,
                        bestMonth?.month_name || "Sin ventas",
                    )}
                    subtitle={
                        bestMonth?.total_ventas
                            ? `${compactNumber.format(bestMonth.total_ventas)} ventas`
                            : "Sin información"
                    }
                />

                <KpiCard
                    title="Mayor crecimiento"
                    value={bestGrowthSede?.sede || "Sin ventas"}
                    subtitle={
                        bestGrowthSede?.crecimiento_porcentaje
                            ? `${percentNumber.format(
                                bestGrowthSede.crecimiento_porcentaje,
                            )}% en ${getMonthNameEs(
                                bestGrowthSede.month,
                                bestGrowthSede.month_name,
                            )}`
                            : "Sin información"
                    }
                    variant="advisor"
                />
            </div>

            <article className="evaluationGlobalChartCard evaluationAnnualChartCard">
                <div className="evaluationGlobalChartHead evaluationAnnualChartHead">
                    <div>
                        <h3>Evolución mensual del año seleccionado</h3>
                        <p>
                            Barras = ventas mensuales · Línea = crecimiento porcentual
                            mensual. Puedes comparar hasta 3 ciudades o 3 sedes.
                        </p>
                    </div>

                    <div className="evaluationAnnualLegendHint">
                        {activeSeries.map((serie, index) => (
                            <span key={serie.value}>
                                <i
                                    style={{
                                        background: COMPARE_COLORS[index % COMPARE_COLORS.length],
                                    }}
                                />
                                {serie.label}
                            </span>
                        ))}
                    </div>
                </div>

                {loading ? (
                    <div className="evaluationGlobalLoading">
                        Cargando análisis anual...
                    </div>
                ) : !hasAnnualData ? (
                    <EmptyChart text="No existen ventas para el año y sedes seleccionadas. Puedes cambiar el año o elegir otra sede." />
                ) : (
                    <ResponsiveContainer width="100%" height={460}>
                        <ComposedChart
                            data={chartData}
                            margin={{ top: 82, right: 28, left: 0, bottom: 18 }}
                        >
                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                            <XAxis
                                dataKey="monthLabel"
                                tick={{ fontSize: 12 }}
                                interval={0}
                                minTickGap={8}
                            />
                            <YAxis
                                yAxisId="sales"
                                tick={{ fontSize: 12 }}
                                allowDecimals={false}
                            />
                            <YAxis
                                yAxisId="growth"
                                orientation="right"
                                tick={{ fontSize: 12 }}
                                tickFormatter={(value) => `${value}%`}
                            />
                            <Tooltip content={<AnnualGrowthTooltip />} />
                            <Legend />

                            {activeSeries.map((serie, index) => {
                                const color = COMPARE_COLORS[index % COMPARE_COLORS.length];
                                const seriesKey = String(serie.value);

                                return (
                                    <Bar
                                        key={`bar-${seriesKey}`}
                                        yAxisId="sales"
                                        dataKey={`sales_${seriesKey}`}
                                        name={`Ventas · ${serie.label}`}
                                        fill={color}
                                        radius={[10, 10, 0, 0]}
                                        maxBarSize={48}
                                    >
                                        <LabelList
                                            dataKey={`sales_${seriesKey}`}
                                            content={(props) =>
                                                renderAnnualBarLabel({
                                                    ...props,
                                                    seriesKey,
                                                    chartData,
                                                })
                                            }
                                        />
                                    </Bar>
                                );
                            })}

                            {activeSeries.map((serie, index) => {
                                const color = COMPARE_COLORS[index % COMPARE_COLORS.length];
                                const seriesKey = String(serie.value);

                                return (
                                    <Line
                                        key={`line-${seriesKey}`}
                                        yAxisId="growth"
                                        type="monotone"
                                        dataKey={`growth_${seriesKey}`}
                                        name={`Crecimiento · ${serie.label}`}
                                        stroke={color}
                                        strokeWidth={3}
                                        dot={<GrowthDot />}
                                        activeDot={{ r: 8, strokeWidth: 3 }}
                                        connectNulls
                                    />
                                );
                            })}
                        </ComposedChart>
                    </ResponsiveContainer>
                )}
            </article>
        </section>
    );
}

function EvaluationGlobalSalesDashboard() {
    const { rol } = useAuth();
    const calendarRef = useRef(null);
    const fpRef = useRef(null);

    const [startDate, setStartDate] = useState(getMonthStart());
    const [endDate, setEndDate] = useState(getToday());
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [analytics, setAnalytics] = useState({
        summary: {},
        byCity: [],
        bySede: [],
        byAdvisor: [],
    });

    const isAdmin = String(rol) === "1";

    useEffect(() => {
        if (!isAdmin || !calendarRef.current) return undefined;

        fpRef.current = flatpickr(calendarRef.current, {
            mode: "range",
            locale: Spanish,
            dateFormat: "Y-m-d",
            defaultDate: [startDate, endDate],
            maxDate: getToday(),
            disableMobile: true,
            closeOnSelect: false,
            onChange: (selectedDates) => {
                if (!selectedDates?.length) return;

                const start = moment(selectedDates[0]).tz(TZ).format("YYYY-MM-DD");
                const end = selectedDates[1]
                    ? moment(selectedDates[1]).tz(TZ).format("YYYY-MM-DD")
                    : start;

                setStartDate(start);
                setEndDate(end);
            },
        });

        return () => {
            fpRef.current?.destroy?.();
            fpRef.current = null;
        };
    }, [isAdmin, startDate, endDate]);

    const normalizeRows = useCallback((rows = []) => {
        return rows.map((row, index) => ({
            ...row,
            name: row.name || "Sin registro",
            shortName: shortName(row.name, 16),
            totalSales: Number(row.totalSales || 0),
            totalAmount: Number(row.totalAmount || 0),
            totalInitial: Number(row.totalInitial || 0),
            avgTicket: Number(row.avgTicket || 0),
            fill: COLORS[index % COLORS.length],
        }));
    }, []);

    const fetchAnalytics = useCallback(async () => {
        if (!isAdmin) return;

        setLoading(true);
        setError("");

        try {
            const token = localStorage.getItem("token");

            const { data } = await axios.get(
                `${API}/evaluation-analytics/global-sales`,
                {
                    headers: { Authorization: `Bearer ${token}` },
                    params: { startDate, endDate },
                },
            );

            setAnalytics({
                summary: data?.summary || {},
                byCity: normalizeRows(data?.byCity || []),
                bySede: normalizeRows(data?.bySede || []),
                byAdvisor: normalizeRows(data?.byAdvisor || []),
            });
        } catch (err) {
            console.error("Error cargando gráficas globales:", err);

            setError(
                err?.response?.data?.error ||
                "No se pudieron cargar las gráficas de ventas globales.",
            );
        } finally {
            setLoading(false);
        }
    }, [endDate, isAdmin, normalizeRows, startDate]);

    useEffect(() => {
        fetchAnalytics();
    }, [fetchAnalytics]);

    const summary = analytics.summary || {};
    const bestAdvisor = summary.bestAdvisor || {};

    const dateLabel = useMemo(
        () => buildCalendarText(startDate, endDate),
        [startDate, endDate],
    );

    if (!isAdmin) return null;

    return (
        <section className="evaluationGlobalSection">
            <div className="evaluationGlobalHeader">
                <div>
                    <span className="evaluationGlobalEyebrow">Administrador</span>
                    <h2>Gráficas de Ventas Globales</h2>
                    <p>{dateLabel}</p>
                </div>

                <div className="evaluationGlobalCalendarBox">
                    <label htmlFor="evaluationGlobalCalendar">Rango de fechas</label>
                    <input
                        id="evaluationGlobalCalendar"
                        ref={calendarRef}
                        readOnly
                        placeholder="Selecciona fechas"
                        value={startDate && endDate ? `${startDate} a ${endDate}` : ""}
                        onChange={() => {}}
                    />
                </div>
            </div>

            {error ? <div className="evaluationGlobalAlert">{error}</div> : null}

            <div className="evaluationGlobalKpiGrid">
                <KpiCard
                    title="Total de ventas"
                    value={compactNumber.format(summary.totalSales || 0)}
                    subtitle="Celulares crédito y contado"
                    variant="primary"
                />

                <KpiCard
                    title="Monto vendido"
                    value={PEN.format(summary.totalAmount || 0)}
                    subtitle="Suma de precio de equipos"
                />

                <KpiCard
                    title="Ciudades con venta"
                    value={compactNumber.format(summary.totalCities || 0)}
                    subtitle="Agrupado por ciudad"
                />

                <KpiCard
                    title="Sedes con venta"
                    value={compactNumber.format(summary.totalSedes || 0)}
                    subtitle="Agrupado por sede"
                />

                <KpiCard
                    title="Mejor asesor"
                    value={bestAdvisor.name || "Sin ventas"}
                    subtitle={
                        bestAdvisor.totalSales
                            ? `${compactNumber.format(bestAdvisor.totalSales)} ventas · ${PEN.format(
                                bestAdvisor.totalAmount || 0,
                            )}`
                            : "Sin información"
                    }
                    variant="advisor"
                />
            </div>

            {loading ? (
                <div className="evaluationGlobalLoading">Cargando gráficas...</div>
            ) : (
                <div className="evaluationGlobalChartsGrid">
                    <article className="evaluationGlobalChartCard">
                        <div className="evaluationGlobalChartHead">
                            <h3>Ventas por ciudad</h3>
                            <p>Participación global de ventas por ciudad.</p>
                        </div>

                        <SalesPieChart data={analytics.byCity} />
                    </article>

                    <article className="evaluationGlobalChartCard">
                        <div className="evaluationGlobalChartHead">
                            <h3>Ventas por sede</h3>
                            <p>Cantidad de ventas por tienda/sede.</p>
                        </div>

                        <SalesBarChart data={analytics.bySede} dataKey="totalSales" />
                    </article>

                    <article className="evaluationGlobalChartCard evaluationGlobalChartWide">
                        <div className="evaluationGlobalChartHead">
                            <h3>Ventas por asesor / vendedor</h3>
                            <p>Ranking de asesores según ventas registradas.</p>
                        </div>

                        <SalesBarChart data={analytics.byAdvisor} dataKey="totalSales" />
                    </article>

                    <article className="evaluationGlobalChartCard evaluationGlobalChartWide">
                        <div className="evaluationGlobalChartHead">
                            <h3>Monto vendido por asesor</h3>
                            <p>
                                Importe total vendido por vendedor en el rango seleccionado.
                            </p>
                        </div>

                        <SalesBarChart
                            data={analytics.byAdvisor}
                            dataKey="totalAmount"
                            money
                        />
                    </article>
                </div>
            )}
        </section>
    );
}

function EvaluationSystem() {
    const { rol } = useAuth();

    const localStorageRemove = [
        "phoneName",
        "creditRecord",
        "phonePrice",
        "initialPriceArr",
        "daysPaymentDays",
        "tcea",
        "initialPricePosition",
        "initialPrice",
        "initialPercent",
        "paymentFrequency",
        "numFee",
        "fee",
        "amountFinance",
        "totalAmountFinance",
        "fullName",
        "lastName",
        "dni",
        "phoneNumber",
        "email",
        "dniFront",
        "dniBack",
        "clientVideo",
        "livingPlace",
        "department",
        "city",
        "location",
        "locationNumber",
        "jobType",
        "jobName",
        "jobDirec",
        "civilStatus",
        "creditCard",
        "dependents",
        "monthlyIncome",
        "monthlyExpenses",
        "contractDate",
        "tycSignContract",
        "daysPayment",
        "idEnrrol",
        "dniEnroll",
        "imei",
        "duplicate",
        "servicePay",
        "loanType",
        "linkNumber",
        "verifyLocation",
    ];

    const [verifyDuplicate] = useState(localStorage.getItem("duplicate") || "");

    useEffect(() => {
        for (let countLS in localStorageRemove) {
            localStorage.removeItem(localStorageRemove[countLS]);
        }

        return () => {
            if (verifyDuplicate === "1") {
                alert("Moviles Pay: El usuario se encuentra financiado.");

                for (let countLS in localStorageRemove) {
                    localStorage.removeItem(localStorageRemove[countLS]);
                }
            }

            localStorage.removeItem("completed");
            localStorage.removeItem("initialPercent");
            localStorage.removeItem("linkNumber");
        };
    }, [verifyDuplicate]);

    return (
        <div>
            {String(rol) === "1" && (
                <>
                    <EvaluationGlobalSalesDashboard />
                    <AnnualGrowthSalesDashboard />
                </>
            )}
        </div>
    );
}

export default EvaluationSystem;
